import { useQueries, type UseQueryResult } from "@tanstack/react-query";
import {
  fetchTrialInsight,
  type AnyInsightsResponse,
  type TrialInsightsParams,
} from "./trialInsightsService";
import type { InsightChartConfig } from "./types";

export type InsightQueryResult = UseQueryResult<AnyInsightsResponse, Error>;

/**
 * Loads every configured insights card.
 *
 * `useQueries` gives one query per card from a config-driven list: they run in
 * parallel, and a slow or failing endpoint never holds up its neighbours.
 * Results come back index-aligned with `charts`.
 *
 * The query key carries every filter, so changing a trials filter refetches
 * the cards alongside the table, and the shared 5-minute staleTime means
 * revisiting the page does not refire them.
 */
export const useTrialInsights = (
  charts: InsightChartConfig[],
  params: TrialInsightsParams
): InsightQueryResult[] => {
  const { orgId, farmId, search, completionFilter, filters } = params;

  return useQueries({
    queries: charts.map((chart) => {
      // A property-scoped endpoint would 400 without one, so that card sits
      // out rather than firing a doomed request.
      const enabled =
        Boolean(orgId) && (!chart.requiresPropertyId || Boolean(farmId));

      return {
        enabled,
        queryKey: [
          "trialInsights",
          chart.id,
          orgId,
          farmId,
          search,
          completionFilter,
          filters,
        ],
        queryFn: async () => await fetchTrialInsight(chart.path, params),
      };
    }),
  });
};
